import axiosConfig from '../../../redux/WeldAnalytics/axiosConfig';
import { getWeldIqPaths } from '../../../redux/WeldAnalytics/apiPaths';

const paths = () => getWeldIqPaths();

let warmupPromise: Promise<void> | null = null;

export interface CheckAnomalyPayload {
  weld_number: string;
  job_number: string;
  pass_name?: string;
  welduid?: string;
  project_id?: number | string;
  warmup?: boolean;
}

export interface CheckIndicationPayload {
  weld_number: string;
  job_number: string;
  pass_name?: string;
  action?: 'ndt';
  welding_system?: string;
  project_id?: number | string;
  warmup?: boolean;
  [key: string]: unknown;
}

export interface PredictPayload {
  weld_number: string;
  job_number: string;
  model?: string;
  threshold?: number;
}

export interface InspectionWeldRow {
  weld_number: string;
  job_number: string;
  welding_system?: string;
  total_passes?: number;
  ok_passes?: number;
  fail_passes?: number;
  pending_passes?: number;
  weld_status?: string;
}

export interface InspectionPassRow {
  pass_name: string;
  welduid?: string | null;
  wps_status?: string;
  welding_system?: string;
  s_time?: number | null;
  c_time?: number | null;
}

export interface PipelineExecution {
  execution_id?: string;
  execution_name?: string;
  status?: string;
  started_at?: string;
  stopped_at?: string;
  pipeline_type?: string;
}

async function postJson<T>(path: string, body: unknown, timeoutMs = 30000): Promise<T> {
  const res = await axiosConfig.post(path, body, { timeout: timeoutMs });
  return res.data as T;
}

async function getJson<T>(path: string, timeoutMs = 30000): Promise<T> {
  const res = await axiosConfig.get(path, { timeout: timeoutMs });
  return res.data as T;
}

/** Pre-load scoring Lambdas once per browser session (shared in-flight promise). */
export async function warmupScoringLambdas(): Promise<void> {
  if (!warmupPromise) {
    const p = paths();
    warmupPromise = Promise.allSettled([
      postJson(p.anomaliesCheck, { warmup: true }, 60000),
      postJson(p.indicationsCheck, { warmup: true }, 60000),
    ]).then(() => undefined);
  }
  return warmupPromise;
}

export function resetWarmupForTests(): void {
  warmupPromise = null;
}

export async function checkAnomaly(payload: CheckAnomalyPayload): Promise<Record<string, unknown>> {
  const p = paths();
  const timeout = payload.pass_name || payload.welduid ? 60000 : 90000;
  return postJson(p.anomaliesCheck, payload, timeout);
}

export async function checkIndication(payload: CheckIndicationPayload): Promise<Record<string, unknown>> {
  const p = paths();
  return postJson(p.indicationsCheck, payload, 60000);
}

export async function predictReject(payload: PredictPayload): Promise<Record<string, unknown>> {
  const p = paths();
  return postJson(p.predict, {
    model: 'xgboost',
    threshold: 0.25,
    ...payload,
  });
}

export async function scorePassRealtime(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const p = paths();
  return postJson(p.realtimeScorePass, body, 60000);
}

export async function fetchInspectionJobs(projectId: number | string): Promise<string[]> {
  const p = paths();
  const data = await getJson<{ jobs?: string[] }>(
    `${p.inspectionJobs}?project_id=${encodeURIComponent(String(projectId))}`,
  );
  return data.jobs ?? [];
}

export async function fetchInspectionWelds(
  projectId: number | string,
  jobNumbers: string[],
): Promise<InspectionWeldRow[]> {
  const p = paths();
  const params = new URLSearchParams();
  params.set('project_id', String(projectId));
  jobNumbers.forEach((jn) => params.append('job_number', jn));
  const data = await getJson<{ welds?: InspectionWeldRow[] }>(
    `${p.inspectionWelds}?${params.toString()}`,
    60000,
  );
  return data.welds ?? [];
}

export async function fetchInspectionPasses(
  projectId: number | string,
  jobNumbers: string[],
  weldNumber: string,
): Promise<InspectionPassRow[]> {
  const p = paths();
  const params = new URLSearchParams();
  params.set('project_id', String(projectId));
  params.set('weld_number', weldNumber);
  jobNumbers.forEach((jn) => params.append('job_number', jn));
  const data = await getJson<{ passes?: InspectionPassRow[] }>(
    `${p.inspectionWelds}?${params.toString()}`,
  );
  return data.passes ?? [];
}

export async function fetchNdtRecordsViaWeldiq(): Promise<Record<string, unknown>[]> {
  const p = paths();
  const data = await postJson<{ ndt_records?: Record<string, unknown>[] }>(p.indicationsCheck, {
    action: 'ndt',
    weld_number: '_',
    job_number: '_',
  });
  return data.ndt_records ?? [];
}

export async function listPipelineExecutions(limit = 10): Promise<PipelineExecution[]> {
  const p = paths();
  const data = await getJson<{ executions?: PipelineExecution[] }>(
    `${p.pipeline}?limit=${limit}`,
  );
  return data.executions ?? [];
}

export async function getPipelineStatus(executionId: string): Promise<Record<string, unknown>> {
  const encoded = encodeURIComponent(executionId);
  const style = process.env.REACT_APP_WELDIQ_API_PATH_STYLE || 'cloudfront';
  const path =
    style === 'gateway'
      ? `pipeline/status/${encoded}`
      : `pipeline-trigger/status/${encoded}`;
  try {
    return await getJson(path);
  } catch {
    return getJson(`pipeline/status/${encoded}`);
  }
}

export async function triggerFullPipeline(): Promise<Record<string, unknown>> {
  const p = paths();
  const res = await axiosConfig.post(p.pipeline, { pipeline_type: 'full' });
  return res.data as Record<string, unknown>;
}
